import {
  CLOCK_SKEW_TOLERANCE_MS,
  WEB_MFA_POLICY,
  WEB_MFA_TTL_MS,
  WEB_MFA_VERIFY_COPY,
  isWebMfaClockExpired,
  requiresWebWeeklyMfa,
  type WebMfaPolicy,
} from '@shared/web-mfa/policy'
import {
  isIndependentUser,
  isMember,
  isProvider,
  resolveProviderLevel,
  type RoleProfile,
} from '@shared/rbac/member-roles'

/**
 * Session-security policy (native soft-lock + web weekly MFA).
 *
 * Native 12h biometric soft-lock is app-owned and does NOT touch Cognito MFA.
 * Web weekly MFA is app-owned email OTP (password + OTP every 7×24h) — also
 * does NOT reconfigure Cognito User Pool MFA.
 */

/** L3+ providers must re-verify with device biometrics every 12 hours. */
export const NATIVE_BIOMETRIC_SOFT_LOCK_TTL_MS = 12 * 60 * 60_000

export { WEB_MFA_TTL_MS, WEB_MFA_VERIFY_COPY, requiresWebWeeklyMfa, isWebMfaClockExpired }
export type { WebMfaPolicy }

/** @deprecated Prefer WEB_MFA_POLICY — kept for import stability. */
export type WebMfaFoundation = WebMfaPolicy

/** Live web MFA policy (enforced on web for eligible roles). */
export const WEB_MFA_FOUNDATION: WebMfaPolicy = WEB_MFA_POLICY

/** Copy for the soft-lock unlock sheet (decision 27C). */
export const SOFT_LOCK_VERIFY_COPY = WEB_MFA_VERIFY_COPY

/**
 * Reason line on the OS biometric / passcode prompt for the app's own lock.
 * Says what the prompt is FOR (it is Ascend's lock, not a phone lock-screen
 * request) so it isn't mistaken for the device passcode screen after a call.
 */
export const SOFT_LOCK_PROMPT_REASON = 'Unlock Ascend to continue'

/**
 * After a call ends while the app is still locked, wait this long before the
 * AUTOMATIC biometric/passcode prompt. Purely a prompt delay: the lock cover,
 * the gated chrome and the Unlock button are unchanged, so nothing in the app
 * becomes reachable without unlocking — the person just isn't ambushed by an
 * OS prompt the instant they hang up.
 */
export const POST_CALL_PROMPT_GRACE_MS = 30_000

/**
 * Mandatory native 12h soft-lock: Provider Admin (L3) and Provider Owner (L4).
 * Members and independents are exempt (21C); L1/L2 are optional soft-prompt only.
 */
export function requiresMandatoryBiometricSoftLock(profile: RoleProfile): boolean {
  if (isMember(profile) || isIndependentUser(profile)) return false
  return isProvider(profile) && resolveProviderLevel(profile) >= 3
}

/**
 * L1/L2 providers may get one soft enrollment prompt per app session (29B).
 * Not mandatory; Skip dismisses for the rest of the session.
 */
export function canOfferOptionalBiometricPrompt(profile: RoleProfile): boolean {
  if (isMember(profile) || isIndependentUser(profile)) return false
  if (!isProvider(profile)) return false
  return resolveProviderLevel(profile) < 3
}

/**
 * Whether a stored unlock timestamp is older than the soft-lock TTL.
 * `null` means never unlocked on this device (treat as overdue once the
 * policy applies / biometric unlock is enrolled). Timestamps in the future
 * beyond a small skew tolerance are treated as expired — a rolled-back device
 * clock (or tampered localStorage) must not disable the 12h policy.
 */
export function isUnlockClockExpired(
  lastUnlockAt: number | null,
  ttlMs: number = NATIVE_BIOMETRIC_SOFT_LOCK_TTL_MS,
  now: number = Date.now(),
): boolean {
  if (lastUnlockAt == null) return true
  if (!Number.isFinite(lastUnlockAt)) return true
  if (lastUnlockAt - now > CLOCK_SKEW_TOLERANCE_MS) return true
  return now - lastUnlockAt >= ttlMs
}
